import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"

// Model Objects

export class BigCommerceOrder extends ModelObject<BigCommerceOrder> {

    public version: number = 0;
    /**
    * The BigCommerce unique identifier for this order.
    */
    public bigCommerceOrderId: number = 0;
    /**
    * The date and time this order was created in BigCommerce.
    */
    public createdAt: Instant = Instant.ofEpochMilli(0);
    /**
    * The total number of items in the order.
    */
    public totalOrderQuantity: number = 0;
    /**
    * The total net value of the order including delivery.
    */
    public totalNetPrice: number = 0;
    private _brewmanOrderId: Guid | null = null;
    /**
    * The Brewman order id assigned to this order on a successful import. If empty, check the error message field for details.
    */
    public get brewmanOrderId(): Guid | null { return this._brewmanOrderId }
    public set brewmanOrderId(value: Guid | null) {
      this._brewmanOrderId = value === undefined ? null : value;
    }
    /**
    * Error information associated with a failed import.
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public errorMessage: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.bigCommerceOrderId = jsonSerializedObject.bigCommerceOrderId;
          this.createdAt = Instant.parse(jsonSerializedObject.createdAt);
          this.totalOrderQuantity = jsonSerializedObject.totalOrderQuantity;
          this.totalNetPrice = jsonSerializedObject.totalNetPrice;
          this.brewmanOrderId = jsonSerializedObject.brewmanOrderId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.brewmanOrderId);
          this.errorMessage = jsonSerializedObject.errorMessage;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.bigCommerceOrderId = this.bigCommerceOrderId;
        toRet.createdAt = this.createdAt.toString();
        toRet.totalOrderQuantity = this.totalOrderQuantity;
        toRet.totalNetPrice = this.totalNetPrice;
        toRet.brewmanOrderId = this.brewmanOrderId == null ? null : this.brewmanOrderId.toString();
        toRet.errorMessage = this.errorMessage;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "bigCommerceOrderId", rule: (v: any) => v == null || v >= -2147483648 || "BigCommerceOrderId is not greater than minimum allowed value: -2147483648"},
            {field: "bigCommerceOrderId", rule: (v: any) => v == null || v <= 2147483647 || "BigCommerceOrderId is above maximum allowed value: 2147483647"},
            {field: "brewmanOrderId", rule: (v: any) => v == null || !v.isEmpty() || "BigCommerceOrder.BrewmanOrderId cannot be empty"},
    ];
}


export class FilteredBigCommerceOrdersResult extends ModelObject<FilteredBigCommerceOrdersResult> {

    public matchingCount: number = 0;
    public bigCommerceOrders: (BigCommerceOrder)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.matchingCount = jsonSerializedObject.matchingCount;
          this.bigCommerceOrders = jsonSerializedObject.bigCommerceOrders.map((v: any) => new BigCommerceOrder(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.matchingCount = this.matchingCount;
        toRet.bigCommerceOrders = this.bigCommerceOrders.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "matchingCount", rule: (v: any) => v == null || v >= -2147483648 || "MatchingCount is not greater than minimum allowed value: -2147483648"},
            {field: "matchingCount", rule: (v: any) => v == null || v <= 2147483647 || "MatchingCount is above maximum allowed value: 2147483647"},
            {field: "bigCommerceOrders", rule: (v: any) => !!v || "bigCommerceOrders is required"},
            {field: "bigCommerceOrders", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as BigCommerceOrder[]).reduce((acc: Array<string>, curr: BigCommerceOrder) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class BigCommerceOrderFilter extends ModelObject<BigCommerceOrderFilter> {

    private _maximumResults: number | null = null;
    public get maximumResults(): number | null { return this._maximumResults }
    public set maximumResults(value: number | null) {
      this._maximumResults = value === undefined ? null : value;
    }
    private _createdFromDate: LocalDate | null = null;
    /**
    * Minimum (inclusive) created at date
    */
    public get createdFromDate(): LocalDate | null { return this._createdFromDate }
    public set createdFromDate(value: LocalDate | null) {
      this._createdFromDate = value === undefined ? null : value;
    }
    private _createdToDate: LocalDate | null = null;
    /**
    * Maximum (inclusive) created at date
    */
    public get createdToDate(): LocalDate | null { return this._createdToDate }
    public set createdToDate(value: LocalDate | null) {
      this._createdToDate = value === undefined ? null : value;
    }
    private _bigCommerceOrderIds: readonly (number)[] | null = null;
    /**
    * Limit to specified BigCommerce order ids
    */
    public get bigCommerceOrderIds(): readonly (number)[] | null { return this._bigCommerceOrderIds }
    public set bigCommerceOrderIds(value: readonly (number)[] | null) {
      this._bigCommerceOrderIds = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.maximumResults = jsonSerializedObject.maximumResults == null ? null : jsonSerializedObject.maximumResults;
          this.createdFromDate = jsonSerializedObject.createdFromDate == null ? null : LocalDate.parse(jsonSerializedObject.createdFromDate);
          this.createdToDate = jsonSerializedObject.createdToDate == null ? null : LocalDate.parse(jsonSerializedObject.createdToDate);
          this.bigCommerceOrderIds = jsonSerializedObject.bigCommerceOrderIds == null ? null : jsonSerializedObject.bigCommerceOrderIds.map((v: any) => v);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.maximumResults = this.maximumResults == null ? null : this.maximumResults;
        toRet.createdFromDate = this.createdFromDate == null ? null : this.createdFromDate.toString();
        toRet.createdToDate = this.createdToDate == null ? null : this.createdToDate.toString();
        toRet.bigCommerceOrderIds = this.bigCommerceOrderIds == null ? null : this.bigCommerceOrderIds.map(v => v);
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "maximumResults", rule: (v: any) => v == null || v >= 0 || "MaximumResults is not greater than minimum allowed value: 0"},
            {field: "maximumResults", rule: (v: any) => v == null || v <= 2147483647 || "MaximumResults is above maximum allowed value: 2147483647"},
    ];
}


export class BigCommerceOrderService extends ServiceBase {

  public async getBigCommerceOrdersByFilter(filter: BigCommerceOrderFilter, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<FilteredBigCommerceOrdersResult> {
    const req = {
    tenantId:api.tenantId.toString(),
    filter:filter.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new FilteredBigCommerceOrdersResult(response.results);;
    return this.makeJsonRequest("bigCommerceOrder/v1/getBigCommerceOrdersByFilter", req, false, extractor, opts);
  };
}

export const bigCommerceOrderService = new BigCommerceOrderService();